import { useEffect, useState } from 'react';
import type { UsernameAvailabilityResult } from '@kangaroo/shared';

import { checkUsernameAvailability } from '../features/auth';

type UsernameAvailabilityState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'ready'; result: UsernameAvailabilityResult };

export function useUsernameAvailability(username: string, enabled: boolean) {
  const [state, setState] = useState<UsernameAvailabilityState>({ status: 'idle' });

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle' });
      return;
    }

    const trimmed = username.trim();
    if (!trimmed) {
      setState({ status: 'idle' });
      return;
    }

    setState({ status: 'checking' });
    const timeout = window.setTimeout(() => {
      void checkUsernameAvailability(trimmed)
        .then((result) => {
          setState({ status: 'ready', result });
        })
        .catch(() => {
          setState({
            status: 'ready',
            result: { available: false, username: trimmed.toLowerCase(), reason: 'invalid' },
          });
        });
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [enabled, username]);

  return state;
}

export function usernameAvailabilityMessage(state: UsernameAvailabilityState): {
  error: boolean;
  text: string;
  success?: boolean;
} {
  if (state.status === 'checking') {
    return { error: false, text: 'Checking availability…' };
  }

  if (state.status !== 'ready') {
    return { error: false, text: '3–32 characters. Letters, numbers, dots, underscores, and hyphens.' };
  }

  if (state.result.available) {
    return { error: false, text: 'Username is available', success: true as const };
  }

  if (state.result.reason === 'taken') {
    return { error: true, text: 'That username is already taken. Try another.' };
  }

  if (state.result.reason === 'reserved') {
    return { error: true, text: 'That username is reserved. Try another.' };
  }

  return { error: true, text: 'Use 3–32 characters. Letters, numbers, dots, underscores, and hyphens.' };
}
